import React, { useState } from 'react';
import { Button, Input, Loader } from 'rsuite';
import { MdLocalOffer, MdCheck, MdClose } from 'react-icons/md';
import axios from 'axios';
import { formatPrice } from '../../services/utilService';

const apiUrl = process.env.REACT_APP_SERVER_API_URL;

const PromoCodeInput = ({
    cartTotal,
    items = [],
    onPromoCodeApplied,
    onPromoCodeRemoved,
    appliedPromoCode = null,
    disabled = false
}) => {
    const [promoCode, setPromoCode] = useState('');
    const [isApplying, setIsApplying] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);

    const applyPromoCode = async () => {
        if (!promoCode.trim()) {
            setErrorMessage('Please enter a promo code');
            return;
        }

        setIsApplying(true);
        setErrorMessage(null);

        try {
            const token = localStorage.getItem('auth-token');
            const userId = localStorage.getItem('user-id');
            
            // Send minimal per-item info so the server can apply per-item
            // percentage rule (take higher of season% vs promo% per item)
            const minimalItems = (items || []).map(item => ({
                week: item.week && item.week.season ? {
                    season: {
                        price: item.week.season.price,
                        discount: item.week.season.discount
                    }
                } : undefined
            }));

            const response = await axios.post(
                `${apiUrl}/promo-codes/validate`,
                {
                    code: promoCode.trim(),
                    userId: userId,
                    cartTotal: cartTotal,
                    items: minimalItems
                },
                {
                    headers: { 'x-auth-token': token }
                }
            );

            if (response.data.valid) {
                const discountInfo = {
                    discount: response.data.discount,
                    originalAmount: response.data.originalAmount,
                    finalAmount: response.data.finalAmount,
                    promoCodeId: response.data.promoCode.id,
                    code: response.data.promoCode.code,
                    description: response.data.promoCode.description,
                    discountType: response.data.promoCode.discountType,
                    serverMessage: response.data.message
                };

                if (onPromoCodeApplied) {
                    onPromoCodeApplied(discountInfo);
                }

                // Clear form after applying
                setPromoCode('');
                setErrorMessage(null);
            } else {
                setErrorMessage(response.data.message);
            }
        } catch (error) {
            if (error.response?.data?.message) {
                setErrorMessage(error.response.data.message);
            } else if (error.response?.status === 404) {
                setErrorMessage('Invalid promo code');
            } else {
                setErrorMessage('Error applying promo code. Please try again.');
            }
        } finally {
            setIsApplying(false);
        }
    };

    const removePromoCode = () => {
        if (onPromoCodeRemoved) {
            onPromoCodeRemoved();
        }
    };

    const handleKeyPress = (event) => {
        if (event.key === 'Enter') {
            applyPromoCode();
        }
    };

    // If there's already an applied promo code, show the applied state
    if (appliedPromoCode) {
        const hasSaving = (appliedPromoCode.discount || 0) > 0;
        const containerClass = hasSaving
            ? 'flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg'
            : 'flex items-center justify-between p-3 bg-yellow-50 border border-yellow-200 rounded-lg';
        const iconColor = hasSaving ? 'text-green-600' : 'text-yellow-600';
        const titleColor = hasSaving ? 'text-green-800' : 'text-yellow-800';
        const subColor = hasSaving ? 'text-green-600' : 'text-yellow-700';
        const btnColor = hasSaving ? 'text-green-600 hover:text-green-800' : 'text-yellow-700 hover:text-yellow-900';

        return (
            <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    <MdLocalOffer className="inline mr-1" />
                    Promo Code
                </label>
                <div className="lg:max-w-md">
                    <div className={containerClass}>
                        <div className="flex items-center space-x-2">
                            <MdCheck className={iconColor} size={20} />
                            <div>
                                <div className={`font-medium ${titleColor}`}>
                                    {appliedPromoCode.code ? appliedPromoCode.code.toUpperCase() : 'Promo code'} applied
                                    {hasSaving ? ` — −$${formatPrice(appliedPromoCode.discount)}` : ''}
                                </div>
                                {!hasSaving && (
                                    <div className={`text-sm ${subColor}`}>
                                        Your existing discount is already as good or better — no additional saving.
                                    </div>
                                )}
                            </div>
                        </div>
                        <Button
                            appearance="ghost"
                            size="sm"
                            onClick={removePromoCode}
                            disabled={disabled}
                            className={btnColor}
                            aria-label="Remove promo code"
                        >
                            <MdClose size={16} />
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="mb-4">
            <div className="mb-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    <MdLocalOffer className="inline mr-1" />
                    Promo Code
                </label>
                <div className="flex gap-2 lg:max-w-md">
                    <Input
                        placeholder="Enter promo code"
                        value={promoCode}
                        onChange={setPromoCode}
                        onKeyPress={handleKeyPress}
                        disabled={disabled || isApplying}
                        className="promo-input flex-1"
                        style={{ textTransform: 'uppercase' }}
                    />
                    <Button
                        appearance="primary"
                        onClick={applyPromoCode}
                        disabled={disabled || isApplying || !promoCode.trim()}
                        className="flex-shrink-0 promo-apply-btn"
                    >
                        {isApplying ? <Loader size="xs" /> : 'Apply'}
                    </Button>
                </div>
            </div>

            {/* Error message */}
            {errorMessage && (
                <p className="text-sm text-red-600 mt-1" role="alert">{errorMessage}</p>
            )}
        </div>
    );
};

export default PromoCodeInput;